import React from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory } from 'react-router-dom';
import { fromJS } from 'immutable';

import { fetchChatSession, joinChatSession } from 'flow/actions/chat-actions';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { PublicSession } from 'flow/types/Chats';
import { routes } from 'flow/types/routes';

import { TypedMap } from 'flow/types/TypedMap';
import { getChatRoomAvatar, getFormattedTime } from 'flow/utils';
import { twMerge } from 'tailwind-merge';
import Button from './Button';
import Icon from './Icon';

interface Props {
    room: TypedMap<PublicSession>;
    className?: string;
}

const Room = ({ room, className }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { ownExtension } = useExtension();
    const { closeModal } = useModal();
    const chatUserKey = ownExtension.get('chatUserKey');

    const isJoined = useAppSelector((state) =>
        state.chats.get('sessions').has(room.get('key')),
    );

    const history = useHistory();

    const navigateToSession = () =>
        history.push(
            generatePath(routes['messages.id'], {
                id: room.get('key'),
            }),
        );

    const handleJoinRoom = () => {
        const charUserDTO = fromJS({ key: chatUserKey });

        const onSuccess = (chatSessionKey: string) => {
            dispatch(fetchChatSession(chatSessionKey, 'contact'));
            navigateToSession();
            closeModal();
        };

        dispatch(joinChatSession(room.get('key'), charUserDTO, onSuccess));
    };

    return (
        <div
            key={room.get('key')}
            className={twMerge(
                'flex flex-col gap-2 rounded-sm bg-white dark:bg-gray-100',
                className,
            )}
        >
            {room.get('roomImageResource') ? (
                <div
                    className="h-40 rounded-t-sm bg-cover"
                    style={{
                        backgroundImage: `url('${getChatRoomAvatar(room?.get('roomImageResource'))}')`,
                    }}
                />
            ) : (
                <div className="flex h-40 items-center justify-center rounded-t-sm bg-gray-200">
                    <Icon
                        iconKey="image"
                        size="3rem"
                        className="text-gray-300"
                    />
                </div>
            )}

            <div className="flex flex-1 flex-col gap-2 overflow-hidden p-6 pt-4">
                <h2 className="text-base/5 font-bold">{room.get('name')}</h2>

                <p className="line-clamp-5 text-sm text-gray-500">
                    {room.get('roomDescription')}
                </p>
            </div>

            <div className="flex items-center justify-between p-6 pt-0">
                <div className="flex items-center gap-x-2">
                    <div className="flex items-center gap-x-1 rounded-sm bg-gray-100 px-1.5 py-1 text-xs">
                        <Icon
                            iconKey="group"
                            className="!size-4 text-gray-600 rtl:scale-x-[-1]"
                        />
                        <p>{room.get('members').size}</p>
                    </div>

                    <time
                        className="flex items-center gap-x-1 rounded-sm bg-gray-100 px-1.5 py-1 text-xs"
                        dateTime={room.getIn(['latestMessage', 'sent'])}
                    >
                        <Icon
                            iconKey="chatBubble"
                            className="!size-4 text-gray-600 rtl:scale-x-[-1]"
                        />

                        {getFormattedTime(
                            room.getIn(['latestMessage', 'sent']),
                            i18next,
                        )}
                    </time>
                </div>

                {isJoined ? (
                    <Button
                        color="ghost"
                        className="!min-w-min px-5 py-1"
                        onClick={() =>
                            history.push(
                                generatePath(routes['messages.id'], {
                                    id: room.get('key'),
                                }),
                            )
                        }
                    >
                        {i18next.t('chat.open-verb')}
                    </Button>
                ) : (
                    <Button
                        color="primary"
                        className="!min-w-min px-5 py-1"
                        onClick={handleJoinRoom}
                    >
                        {i18next.t('chat.joinChatRoom')}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default Room;
